import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, ClipboardCheck, Factory, FileText, History, Loader2, Package, ShieldCheck, Thermometer, Truck, XCircle } from "lucide-react";
import api from "../../lib/api";
import { formatItalianDate, formatItalianDateTime } from "../../lib/dateUtils";
import { Logo } from "../../components/Logo";

// Scheda pubblica del QR materie prime (routers_raw_material_qr.py) — nessuna
// autenticazione, nessun dato personale. Il QR è fisso per materia prima+sede:
// mostra sempre i lotti in uso in quel momento (tracciabilità in entrata:
// lotto, fornitore, scadenza, DDT di appartenenza, controlli al ricevimento),
// le produzioni in cui sono stati usati e lo storico dei lotti precedenti.
const EXPIRY = {
  valido: { chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", bar: "bg-emerald-500", icon: CheckCircle2 },
  in_scadenza: { chip: "bg-amber-50 text-amber-800 ring-amber-200", bar: "bg-amber-500", icon: CalendarClock },
  scaduto: { chip: "bg-red-50 text-red-700 ring-red-200", bar: "bg-red-500", icon: XCircle },
  non_indicata: { chip: "bg-slate-100 text-slate-600 ring-slate-200", bar: "bg-slate-400", icon: AlertTriangle },
};

const expiryText = ({ stato, giorni }) => {
  if (stato === "non_indicata") return "Scadenza non indicata";
  if (stato === "scaduto") return giorni === -1 ? "Scaduto da 1 giorno" : `Scaduto da ${Math.abs(giorni)} giorni`;
  if (giorni === 0) return "Scade oggi";
  if (giorni === 1) return "Scade domani";
  return `Valido ancora ${giorni} giorni`;
};

const HEADER = {
  valido: { label: "Lotto in uso valido", cls: "bg-emerald-400/15 text-emerald-200 ring-emerald-300/30" },
  in_scadenza: { label: "Lotto in scadenza", cls: "bg-amber-400/15 text-amber-200 ring-amber-300/30" },
  scaduto: { label: "Attenzione: lotto scaduto", cls: "bg-red-400/20 text-red-200 ring-red-300/40" },
  non_indicata: { label: "Scadenza da completare", cls: "bg-amber-400/15 text-amber-200 ring-amber-300/30" },
  nessun_lotto: { label: "Nessun lotto in uso", cls: "bg-white/10 text-white/80 ring-white/20" },
};

export default function RawMaterialPublic() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    api.get(`/public/materia-prima/${token}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col items-center bg-haccp-bg px-4 py-6">
      <div className="w-full max-w-md">
        <div className="my-6 flex justify-center"><Logo size="md" /></div>
        {data === undefined ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : data === null ? (
          <div className="rounded-2xl bg-white p-8 text-center shadow-card" data-testid="raw-trace-invalid">
            <XCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
            <h1 className="font-heading text-xl font-bold">QR non valido</h1>
            <p className="mt-2 font-body text-slate-500">Il codice scansionato non corrisponde ad alcuna materia prima.</p>
          </div>
        ) : <Sheet data={data} />}
      </div>
    </div>
  );
}

function Sheet({ data }) {
  const head = HEADER[data.stato] || HEADER.nessun_lotto;
  const [main, ...others] = data.lotti_in_uso || [];
  return (
    <div className="animate-fade-up overflow-hidden rounded-2xl bg-white shadow-card" data-testid="raw-trace">
      <div className="relative overflow-hidden bg-navy p-6 text-white">
        <Package className="absolute -right-4 -top-4 h-28 w-28 text-white/5" strokeWidth={1} aria-hidden="true" />
        <div className="text-xs uppercase tracking-[0.2em] text-gold-bright">Tracciabilità in entrata</div>
        <h1 className="mt-1 font-heading text-2xl font-bold leading-tight">{data.materia_prima}</h1>
        <div className="mt-1 text-sm text-white/70">{data.azienda}{data.sede ? ` · ${data.sede}` : ""}</div>
        <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${head.cls}`} data-testid="raw-trace-status">
          <span className="h-1.5 w-1.5 rounded-full bg-current" /> {head.label}
        </span>
      </div>

      <div className="space-y-5 p-5">
        {data.allergeni?.length > 0 && (
          <div className="rounded-lg bg-red-50 p-3 ring-1 ring-red-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700"><AlertTriangle className="h-4 w-4" /> Allergeni</div>
            <div className="mt-1 text-sm text-red-700">{data.allergeni.join(", ")}</div>
          </div>
        )}
        {data.tracce?.length > 0 && (
          <div className="rounded-lg bg-amber-50 p-3 ring-1 ring-amber-100">
            <div className="text-sm font-semibold text-amber-700">Può contenere tracce di</div>
            <div className="mt-1 text-sm text-amber-700">{data.tracce.join(", ")}</div>
          </div>
        )}

        {main ? <>
          <LotCard lot={main} primary />
          {others.length > 0 && (
            <div data-testid="raw-trace-other-lots">
              <SectionTitle icon={Package}>Altri lotti in uso ({others.length})</SectionTitle>
              <p className="-mt-1 mb-2 text-xs text-slate-500">In ordine di scadenza: si usa per primo quello che scade prima.</p>
              <div className="space-y-3">{others.map((lot, i) => <LotCard key={`${lot.lotto}-${i}`} lot={lot} />)}</div>
            </div>
          )}
        </> : (
          <div className="rounded-xl bg-haccp-bg p-5 text-center ring-1 ring-hairline" data-testid="raw-trace-empty">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p className="font-medium text-navy">Nessun lotto in uso per questa materia prima.</p>
            <p className="mt-1 text-sm text-slate-500">La scheda si aggiorna automaticamente al prossimo lotto registrato.</p>
          </div>
        )}

        {data.storico?.length > 0 && <HistoryList lots={data.storico} />}

        <div className="space-y-1 border-t border-hairline pt-4 text-xs text-slate-500">
          <div className="font-semibold text-navy">{data.azienda}</div>
          {data.azienda_indirizzo && <div>{data.azienda_indirizzo}</div>}
          {data.azienda_piva && <div>P.IVA {data.azienda_piva}</div>}
        </div>
      </div>
      <div className="flex items-start gap-2 bg-haccp-bg px-5 py-4 text-xs text-slate-500">
        <ShieldCheck className="h-4 w-4 shrink-0 text-gold" />
        <span>Scheda sempre aggiornata all’ultimo lotto registrato (consultata il {formatItalianDateTime(data.aggiornato_il)}). Informazioni di tracciabilità autorizzate, nessun dato personale.</span>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return <div className="mb-2 flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4 text-gold" /> {children}</div>;
}

function LotCard({ lot, primary = false }) {
  const exp = EXPIRY[lot.scadenza_stato?.stato] || EXPIRY.non_indicata;
  const ExpIcon = exp.icon;
  const doc = lot.documento || {};
  return (
    <div className={`overflow-hidden rounded-xl ring-1 ${primary ? "ring-navy/15 shadow-sm" : "ring-hairline"}`} data-testid={primary ? "raw-trace-main-lot" : "raw-trace-lot"}>
      <div className={`h-1 ${exp.bar}`} />
      <div className="p-4">
        {primary && <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Lotto in uso</div>}
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={`font-mono font-bold text-navy ${primary ? "text-2xl" : "text-lg"}`} data-testid="raw-trace-lot-code">{lot.lotto || "—"}</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${exp.chip}`}><ExpIcon className="h-3.5 w-3.5" /> {expiryText(lot.scadenza_stato || {})}</span>
        </div>
        {lot.codice_prodotto && <div className="mt-0.5 text-xs text-slate-500">Codice prodotto {lot.codice_prodotto}</div>}

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Item icon={CalendarClock} label="Scadenza / TMC" value={formatItalianDate(lot.scadenza)} />
          <Item icon={Truck} label="Ricevuto il" value={formatItalianDate(lot.ricevuto_il)} />
          <Item icon={Factory} label="Fornitore" value={lot.fornitore || <span className="text-red-600">Non indicato</span>} wide />
          <Item icon={FileText} label="Documento di trasporto" wide value={doc.numero
            ? <>{doc.tipo} n. <strong>{doc.numero}</strong>{doc.data ? ` del ${formatItalianDate(doc.data)}` : ""}{doc.in_attesa && <span className="block text-xs text-amber-700">Documento in attesa di essere allegato</span>}</>
            : <span className="text-amber-700">{doc.allegato ? "Documento allegato in archivio (numero non indicato)" : "Riferimento DDT non indicato"}</span>} />
        </dl>

        {lot.accettazione && <Acceptance data={lot.accettazione} />}

        {lot.produzioni_totali > 0 && (
          <div className="mt-3 border-t border-hairline pt-3">
            <div className="mb-1.5 text-xs font-semibold text-slate-500">Usato in {lot.produzioni_totali} {lot.produzioni_totali === 1 ? "produzione" : "produzioni"}</div>
            <div className="flex flex-wrap gap-1.5">
              {lot.produzioni.map((p) => {
                const label = <><span className="font-mono">{p.lotto}</span> · {p.prodotto}</>;
                return p.token
                  ? <a key={p.lotto} href={`/t/${p.token}`} className="inline-flex min-h-[32px] items-center rounded-md bg-haccp-bg px-2 text-[11px] text-navy ring-1 ring-hairline hover:ring-gold">{label}</a>
                  : <span key={p.lotto} className="inline-flex min-h-[32px] items-center rounded-md bg-haccp-bg px-2 text-[11px] text-navy ring-1 ring-hairline">{label}</span>;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Item({ icon: Icon, label, value, wide = false }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</dt>
      <dd className="mt-0.5 text-navy">{value}</dd>
    </div>
  );
}

const CHECK_LABEL = { si: "OK", no: "Anomalia", na: "N/A" };

function Acceptance({ data }) {
  return (
    <div className={`mt-3 rounded-lg p-3 ring-1 ${data.conforme ? "bg-emerald-50/60 ring-emerald-100" : "bg-amber-50 ring-amber-200"}`} data-testid="raw-trace-acceptance">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-navy"><ClipboardCheck className="h-4 w-4 text-gold" /> Controllo al ricevimento</span>
        <span className={`text-xs font-semibold ${data.conforme ? "text-emerald-700" : "text-amber-800"}`}>{data.conforme ? "Merce accettata · conforme" : "Accettata con anomalie"}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {data.temperatura_c != null && <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-navy ring-1 ring-hairline"><Thermometer className="h-3 w-3" /> {data.temperatura_c} °C</span>}
        {data.controlli.map((c) => (
          <span key={c.voce} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${c.esito === "no" ? "bg-red-50 text-red-700 ring-red-200" : "bg-white text-navy ring-hairline"}`}>{c.voce}: {CHECK_LABEL[c.esito] || c.esito}</span>
        ))}
      </div>
    </div>
  );
}

function HistoryList({ lots }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl ring-1 ring-hairline" data-testid="raw-trace-history">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-navy"><History className="h-4 w-4 text-gold" /> Storico lotti precedenti ({lots.length})</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="space-y-0 border-t border-hairline px-4 py-2">
          {lots.map((lot, i) => (
            <li key={`${lot.lotto}-${i}`} className="relative border-l-2 border-slate-200 py-2 pl-4">
              <span className="absolute -left-[5px] top-3.5 h-2 w-2 rounded-full bg-slate-300" />
              <div className="flex flex-wrap justify-between gap-2"><span className="font-mono text-sm font-semibold text-navy">{lot.lotto || "—"}</span><span className="text-xs text-slate-500">Ricevuto il {formatItalianDate(lot.ricevuto_il)}</span></div>
              <div className="text-xs text-slate-600">{lot.fornitore || "Fornitore non indicato"} · Scad. {formatItalianDate(lot.scadenza)}</div>
              <div className="text-xs text-slate-500">{lot.documento?.numero ? `${lot.documento.tipo} n. ${lot.documento.numero}` : "DDT non indicato"}{lot.chiuso_il ? ` · esaurito il ${formatItalianDate(lot.chiuso_il)}` : ""}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
